import { ConfigProvider, Layout, Spin, theme } from 'antd';
import ruRU from 'antd/locale/ru_RU';
import { Navigate, Route, Routes } from 'react-router-dom';
import {
  Agent,
  AgentTool,
  AgentTools,
  Agents,
  Article,
  Articles,
  Auth,
  Channel,
  Channels,
  Home,
  Space,
  Spaces,
  User,
  Users,
  Welcome,
} from '@components';
import { AuthProvider, useAuth } from '@context/auth-context';
import MainMenu from './components/main-menu/main-menu';
import './styles.css';

const { Content } = Layout;

const AppContent = () => {
  const { isAuthenticated, isCheckingSession, isAdmin, logout } = useAuth();

  if (isCheckingSession) {
    return (
      <div className="app-loader">
        <Spin size="large" tip="Проверяем сессию" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Auth />;
  }

  return (
    <Layout className="app-layout">
      <MainMenu isAdmin={isAdmin} onLogout={logout} />

      <Content className="app-content">
        <Routes>
          <Route path="/" element={<Welcome />} />
          <Route path="/dialogs" element={<Home />} />
          <Route path="/dialogs/:dialogId" element={<Home />} />
          <Route path="/channels" element={isAdmin ? <Channels /> : <Navigate to="/" replace />} />
          <Route path="/channels/new" element={isAdmin ? <><Channels /><Channel /></> : <Navigate to="/" replace />} />
          <Route path="/channels/:id" element={isAdmin ? <><Channels /><Channel /></> : <Navigate to="/" replace />} />
          <Route path="/agents" element={isAdmin ? <Agents /> : <Navigate to="/" replace />} />
          <Route path="/agents/new" element={isAdmin ? <><Agents /><Agent /></> : <Navigate to="/" replace />} />
          <Route path="/agents/:agentId/tools" element={isAdmin ? <><Agents /><AgentTools /></> : <Navigate to="/" replace />} />
          <Route path="/agents/:agentId/tools/new" element={isAdmin ? <><Agents /><AgentTool /></> : <Navigate to="/" replace />} />
          <Route path="/agents/:agentId/tools/:toolId" element={isAdmin ? <><Agents /><AgentTool /></> : <Navigate to="/" replace />} />
          <Route path="/agents/:id" element={isAdmin ? <><Agents /><Agent /></> : <Navigate to="/" replace />} />
          <Route path="/agent-tools" element={isAdmin ? <AgentTools /> : <Navigate to="/" replace />} />
          <Route path="/agent-tools/new" element={isAdmin ? <><AgentTools /><AgentTool /></> : <Navigate to="/" replace />} />
          <Route path="/agent-tools/:id" element={isAdmin ? <><AgentTools /><AgentTool /></> : <Navigate to="/" replace />} />
          <Route path="/users" element={isAdmin ? <Users /> : <Navigate to="/" replace />} />
          <Route path="/users/new" element={isAdmin ? <><Users /><User /></> : <Navigate to="/" replace />} />
          <Route path="/users/:id" element={isAdmin ? <><Users /><User /></> : <Navigate to="/" replace />} />
          <Route path="/spaces" element={isAdmin ? <Spaces /> : <Navigate to="/" replace />} />
          <Route path="/spaces/new" element={isAdmin ? <><Spaces /><Space /></> : <Navigate to="/" replace />} />
          <Route path="/spaces/:id" element={isAdmin ? <><Spaces /><Space /></> : <Navigate to="/" replace />} />
          <Route path="/articles" element={isAdmin ? <Articles /> : <Navigate to="/" replace />} />
          <Route path="/articles/new" element={isAdmin ? <><Articles /><Article /></> : <Navigate to="/" replace />} />
          <Route path="/articles/:id" element={isAdmin ? <><Articles /><Article /></> : <Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Content>
    </Layout>
  );
};

const App = () => {
  return (
    <ConfigProvider
      locale={ruRU}
      theme={{
        algorithm: theme.defaultAlgorithm,
        token: {
          borderRadius: 12,
          borderRadiusLG: 16,
          borderRadiusSM: 10,
          colorPrimary: '#0f766e',
          colorInfo: '#0f766e',
          colorSuccess: '#059669',
          colorBgLayout: '#eef3f6',
          colorBgContainer: '#ffffff',
          colorText: '#12202b',
          colorTextSecondary: '#5b6f7c',
          colorBorder: '#d7e2e8',
          colorBorderSecondary: '#e6eef2',
          fontFamily: '"Manrope", "Segoe UI", sans-serif',
          fontSize: 14,
          controlHeight: 32,
          controlHeightSM: 28,
          controlHeightLG: 36,
          boxShadow: '0 10px 30px rgba(15, 35, 45, 0.06)',
          boxShadowSecondary: '0 4px 16px rgba(15, 35, 45, 0.05)',
        },
        components: {
          Button: {
            borderRadius: 6,
            primaryShadow: '0 8px 18px rgba(15, 118, 110, 0.22)',
            fontWeight: 600,
            paddingInline: 12,
          },
          Form: {
            itemMarginBottom: 10,
            verticalLabelPadding: '0 0 2px',
          },
          Input: {
            paddingBlock: 4,
            paddingInline: 10,
          },
          InputNumber: {
            paddingBlock: 2,
            paddingInline: 8,
          },
          Select: {
            optionPadding: '5px 10px',
          },
          Drawer: {
            paddingLG: 12,
          },
          Card: {
            headerFontSize: 16,
          },
          Menu: {
            itemBorderRadius: 12,
            itemMarginInline: 8,
            itemHeight: 52,
          },
          Table: {
            headerBg: '#f5f9fb',
            rowHoverBg: '#f3faf8',
          },
          Tag: {
            defaultBg: '#eef6f4',
            defaultColor: '#0f4c4a',
          },
        },
      }}
    >
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ConfigProvider>
  );
};

export default App;
